// @figma file=pkCGby54zXzTsccXvHqzO6 node=tbd name="FormWizard" set="mytaible-shadcn-studio" verified=tbd

import { CheckIcon } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FormWizardStep {
  id: string;
  title: string;
  description?: string;
}

export interface FormWizardProps extends React.HTMLAttributes<HTMLDivElement> {
  steps: FormWizardStep[];
  currentStep: number;
  onStepChange?: (step: number) => void;
  layout?: 'horizontal' | 'vertical';
}

// ─── FormWizard ───────────────────────────────────────────────────────────────

const FormWizard = React.forwardRef<HTMLDivElement, FormWizardProps>(
  (
    { className, steps, currentStep, onStepChange, layout = 'horizontal', children, ...props },
    ref,
  ) => {
    const isHorizontal = layout === 'horizontal';

    return (
      <div
        ref={ref}
        className={cn('flex w-full', isHorizontal ? 'flex-col gap-8' : 'flex-row gap-8', className)}
        {...props}
      >
        {/* Step indicators */}
        <nav
          aria-label="Form wizard steps"
          className={cn(isHorizontal ? 'flex items-start justify-between' : 'flex flex-col gap-2')}
        >
          {steps.map((step, index) => {
            const status =
              index < currentStep ? 'completed' : index === currentStep ? 'active' : 'pending';

            return (
              <React.Fragment key={step.id}>
                {/* Step item */}
                <button
                  type="button"
                  onClick={() => onStepChange?.(index)}
                  disabled={index > currentStep}
                  aria-current={status === 'active' ? 'step' : undefined}
                  className={cn(
                    'flex items-center gap-3 text-left',
                    isHorizontal ? 'flex-col items-center text-center' : 'flex-row',
                    index > currentStep ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                  )}
                >
                  {/* Circle */}
                  <div
                    className={cn(
                      'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-medium transition-colors',
                      status === 'active' && 'bg-primary text-primary-foreground',
                      status === 'completed' && 'bg-primary/20 text-primary',
                      status === 'pending' && 'bg-muted text-muted-foreground',
                    )}
                  >
                    {status === 'completed' ? (
                      <CheckIcon className="h-4 w-4" aria-hidden="true" />
                    ) : (
                      <span>{index + 1}</span>
                    )}
                  </div>
                  {/* Label */}
                  <div>
                    <p
                      className={cn(
                        'text-sm font-medium',
                        status === 'active' && 'text-foreground',
                        status === 'completed' && 'text-foreground',
                        status === 'pending' && 'text-muted-foreground',
                      )}
                    >
                      {step.title}
                    </p>
                    {step.description && (
                      <p className="text-xs text-muted-foreground">{step.description}</p>
                    )}
                  </div>
                </button>

                {/* Connector */}
                {index < steps.length - 1 && (
                  <div
                    aria-hidden="true"
                    className={cn(
                      'shrink-0 bg-border',
                      isHorizontal ? 'mx-2 mt-4 h-px flex-1' : 'ml-4 my-1 h-6 w-px',
                    )}
                  />
                )}
              </React.Fragment>
            );
          })}
        </nav>

        {/* Step content */}
        {children && <div className="min-w-0 flex-1">{children}</div>}
      </div>
    );
  },
);
FormWizard.displayName = 'FormWizard';

export { FormWizard };
